/**
 * The order Ocean Statuses are read in, and the date each one is judged by.
 *
 * Alan gave this as the accurate ordering on 2026-08-08, numbered 1 to 9. It runs
 * from the work that is nearly finished to the work that has not started, because
 * the top of the table is where the day's attention goes: an empty already
 * reported is one phone call from done, a box that has not reached the port is
 * not today's problem.
 *
 * Each status is sorted by the date that actually governs it - the same reason
 * the order exists. A reported empty is timed by when the customer reported it;
 * something available for pickup is timed by its appointment, and by its last
 * free day when no appointment has been made. Sorting all nine by one shared
 * date would put a box with no ETA above one whose free time runs out tomorrow.
 *
 * Measured over production the same day, 452 live containers:
 *
 *     277  not_arrived_at_port      61  available_for_pickup
 *      37  empty_return_reported    32  inspection
 *      24  pending                  19  delivered_to_warehouse
 *       1  cancelled                 1  no ocean_status at all
 *
 * transfer and empty_returned are in the order and absent from the data. They
 * are kept because the order is the contract, not a description of one snapshot.
 */
export const OCEAN_STATUS_ORDER = Object.freeze([
  Object.freeze({ rank: 1, token: 'empty_return_reported', dateFields: Object.freeze(['reported_empty_at']) }),
  Object.freeze({ rank: 2, token: 'delivered_to_warehouse', dateFields: Object.freeze(['pickup_appointment_at']) }),
  Object.freeze({ rank: 3, token: 'transfer', dateFields: Object.freeze(['transfer_at']) }),
  // Last free day first (Alan, 2026-08-08: "可提部分是按照 lfd"). Once a box is
  // available, what decides the order of work is when the free time runs out -
  // the appointment is only a fallback for the ones that have no LFD on file,
  // which on 2026-08-08 was 320 of 452 containers.
  Object.freeze({ rank: 4, token: 'available_for_pickup', dateFields: Object.freeze(['lfd_at', 'pickup_appointment_at']) }),
  // 查验放行 reads above 查验 (Alan, 2026-08-12). Customs has let the box go, so it
  // is a step from being workable; one still being examined is not today's work at
  // all. It goes between 可提 and 查验 at 4.5 rather than renumbering, the same way
  // UNRANKED below sits at 6.5 - Alan's 1-to-9 order is the contract and stays as
  // he gave it. Both inspection states are judged on the same date, so a released
  // box and a held one still read in one ETA order within their own group.
  Object.freeze({ rank: 4.5, token: 'inspection_cleared', dateFields: Object.freeze(['eta_at']) }),
  Object.freeze({ rank: 5, token: 'inspection', dateFields: Object.freeze(['eta_at']) }),
  Object.freeze({ rank: 6, token: 'pending', dateFields: Object.freeze(['eta_at']) }),
  Object.freeze({ rank: 7, token: 'not_arrived_at_port', dateFields: Object.freeze(['eta_at']) }),
  Object.freeze({ rank: 7, token: 'not_arrived', dateFields: Object.freeze(['eta_at']) }),
  Object.freeze({ rank: 8, token: 'empty_returned', dateFields: Object.freeze(['actual_empty_returned_at', 'empty_returned_at']) }),
  Object.freeze({ rank: 8, token: 'empty_returned_to_terminal', dateFields: Object.freeze(['actual_empty_returned_at', 'empty_returned_at']) }),
  Object.freeze({ rank: 9, token: 'cancelled', dateFields: Object.freeze([]) }),
  Object.freeze({ rank: 9, token: 'canceled', dateFields: Object.freeze([]) }),
]);

/**
 * The statuses a person may choose, in the order above.
 *
 * Alan, 2026-08-09: "所有的 status 是要对照这个 已报空，已送仓，转仓，可提，查验，
 * pending，未到港，已还空，取消。必须包含这些状态". This list is that sentence, and
 * every menu, filter and legend in the product is built from it - so the words on
 * the screen are the words he wrote, and nothing else is offered.
 *
 * `inspection_cleared` (查验放行) is the tenth, added back on 2026-08-12 at Alan's
 * ask. It had come off the menu on 2026-08-09 for not being one of the nine, but
 * it is not a stray alias: 查验放行 is a column in the workbook this data was
 * imported from - `ocean_status_source` reads "5查验放行" on the three containers
 * that carried it in the 2026-07-28 import - and it already had a place in the
 * order, a colour and a row tint. What it lacked was a way to choose it.
 */
export const OCEAN_STATUS_TOKENS = Object.freeze([
  'empty_return_reported',
  'delivered_to_warehouse',
  'transfer',
  'available_for_pickup',
  'inspection_cleared',
  'inspection',
  'pending',
  'not_arrived_at_port',
  'empty_returned',
  'cancelled',
]);

const BY_TOKEN = new Map(OCEAN_STATUS_ORDER.map((entry) => [entry.token, entry]));

// A status nobody listed sits between the work in progress and the work that has
// not started, rather than at either end: it is unclassified, not urgent and not
// finished. One container in production carries no ocean_status at all.
const UNRANKED = Object.freeze({ rank: 6.5, token: '', dateFields: Object.freeze(['eta_at']) });

export function oceanStatusToken(row) {
  const value = String(row?.ocean_status ?? '').trim().toLowerCase();
  if (value) return value;
  // A completed container may only say so on its own status field.
  const status = String(row?.status ?? '').trim().toLowerCase();
  return BY_TOKEN.has(status) ? status : '';
}

export function oceanStatusEntry(token) {
  return BY_TOKEN.get(String(token ?? '').trim().toLowerCase()) ?? UNRANKED;
}

export function oceanStatusRank(token) {
  return oceanStatusEntry(token).rank;
}

function millis(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value?.toDate === 'function') return millis(value.toDate());
  if (Number.isFinite(value)) return Number(value);
  return null;
}

/**
 * When this row is due, by whichever date governs its status. A row with none of
 * them sorts to the end of its own group rather than jumping the queue.
 */
export function oceanStatusDeadline(row, token = oceanStatusToken(row)) {
  for (const field of oceanStatusEntry(token).dateFields) {
    const value = millis(row?.[field]);
    if (value !== null) return value;
  }
  return Number.MAX_SAFE_INTEGER;
}

/**
 * The table order: by status rank, then by the date that status is judged on,
 * then by id so the sort is stable across reloads.
 */
export function compareByOceanStatusOrder(left, right) {
  const leftToken = oceanStatusToken(left);
  const rightToken = oceanStatusToken(right);
  const rank = oceanStatusRank(leftToken) - oceanStatusRank(rightToken);
  if (rank !== 0) return rank;
  const deadline = oceanStatusDeadline(left, leftToken) - oceanStatusDeadline(right, rightToken);
  if (deadline !== 0) return deadline;
  return String(left?.id ?? '').localeCompare(String(right?.id ?? ''));
}

/**
 * Sorting by one chosen date instead of by status.
 *
 * The Filters panel has the last word on order (Alan, 2026-08-08): picking a
 * timeline there is asking to read the table in that date's order, so it
 * outranks the per-status default above. Rows with no value for that date go to
 * the bottom rather than the top - an absent date is not the earliest date.
 */
export function compareByTimeline(field) {
  const key = String(field ?? '').trim();
  if (!key) return compareByOceanStatusOrder;
  return (left, right) => {
    const leftValue = millis(left?.[key]);
    const rightValue = millis(right?.[key]);
    if (leftValue === null && rightValue === null) return compareByOceanStatusOrder(left, right);
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;
    return leftValue - rightValue
      || String(left?.id ?? '').localeCompare(String(right?.id ?? ''));
  };
}

/**
 * How many containers each company holds in each status, in the reading order
 * above. Only statuses actually present come back - a card listing six zeroes
 * next to two real numbers buries the two.
 */
export function oceanStatusCounts(rows) {
  const counts = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const token = oceanStatusToken(row) || 'unknown';
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([token, count]) => ({ token, count, rank: oceanStatusRank(token) }))
    .sort((left, right) => left.rank - right.rank || right.count - left.count
      || left.token.localeCompare(right.token))
    .map((entry) => Object.freeze(entry));
}
